/**
 * 편집 동작 (P1-②) — Enter(블록 분할/리스트) · Backspace(병합/타입 해제), IME 가드.
 * 샘플 block-editor.html의 handleEnter/handleBackspace 패턴 이식.
 *
 *  - Enter: codeBlock=줄바꿈, 빈 리스트항목=문단으로, 그 외=caret에서 분할(리스트는 타입 유지).
 *  - Backspace(블록 시작·collapsed): 비문단=문단으로, 문단=이전 블록에 병합(앞이 표면 마지막 셀로).
 *  - IME 가드: e.isComposing 또는 에디터 조합상태면 무시(한글 조합 무손상).
 *  - 표 셀(.lumir-tbl 내부) keydown은 처리하지 않음(P3).
 *
 * 구조 변경 후 opts.onChange()로 히스토리 커밋/알림을 알린다(프로그램적 DOM 변경은 input 미발생).
 */

import { makeBlockElement, directContent, LIST_TYPES } from "./serialize/block.js";
import { applyInlineMark } from "./inline-format.js";

const BASE_PROPS = () => ({ textColor: "default", backgroundColor: "default", textAlignment: "left" });
function defaultProps(type) {
  if (type === "checkListItem") return { ...BASE_PROPS(), checked: false };
  if (type === "codeBlock") return { language: "" };
  return BASE_PROPS();
}

function focusStart(el) {
  if (!el) return;
  el.focus();
  const r = document.createRange();
  r.selectNodeContents(el);
  r.collapse(true);
  const s = window.getSelection();
  s.removeAllRanges();
  s.addRange(r);
}
function focusEnd(el) {
  if (!el) return;
  el.focus();
  const r = document.createRange();
  r.selectNodeContents(el);
  r.collapse(false);
  const s = window.getSelection();
  s.removeAllRanges();
  s.addRange(r);
}
function caretAtStart(content) {
  const s = window.getSelection();
  if (!s.rangeCount) return false;
  const a = s.getRangeAt(0);
  const r = a.cloneRange();
  r.selectNodeContents(content);
  r.setEnd(a.startContainer, a.startOffset);
  return r.toString().length === 0;
}

/**
 * content 후행 "\n" 한 개 제거(코드블록 탈출 시 트리거 개행 정리).
 * 브라우저 Range.insertNode는 텍스트 노드를 분할하며 "\n" 뒤에 **빈 텍스트 노드**를 남길 수 있다
 * → 그대로 마지막 텍스트 노드를 보면 ""라 "\n"을 못 찾는다. filler <br>·빈 노드를 걷어내고
 * normalize()로 인접 텍스트를 병합한 뒤 마지막 텍스트 노드의 후행 "\n"을 확실히 제거한다.
 */
function deleteTrailingNewline(content) {
  content.querySelectorAll("br[data-lumir-br-filler]").forEach((b) => b.remove());
  content.normalize(); // 인접/빈 텍스트 노드 병합 → 후행 "\n"이 마지막 텍스트 노드 끝에 오게
  const walker = document.createTreeWalker(content, NodeFilter.SHOW_TEXT, null);
  let last = null, n;
  while ((n = walker.nextNode())) last = n;
  if (last && last.nodeValue.endsWith("\n")) {
    last.nodeValue = last.nodeValue.slice(0, -1);
    if (last.nodeValue === "") last.remove();
  }
}

/**
 * 코드블록 후행 개행 렌더 보정 — `white-space:pre-wrap`에서 문자열 끝의 단일 "\n"은
 * 빈 마지막 줄을 그리지 않는 브라우저 특성이 있다(<textarea>·<pre>와 동일). 그래서
 * 텍스트가 "\n"으로 끝나면 직렬화에서 무시되는 filler <br>를 끝에 덧대 빈 줄을 보이게 한다.
 * (filler <br>는 walkInline에서 data-lumir-br-filler로 걸러져 라운드트립 무오염)
 * 내용에 후행 개행이 없으면 오래된 filler를 제거한다.
 */
export function normalizeCodeTrailingNewline(content) {
  if (!content) return;
  content.querySelectorAll('br[data-lumir-br-filler]').forEach((b) => b.remove());
  if ((content.textContent || "").endsWith("\n")) {
    const filler = document.createElement("br");
    filler.setAttribute("data-lumir-br-filler", "");
    content.appendChild(filler);
  }
}

/**
 * 분할 후 남는 **빈 인라인 서식 래퍼**(<code></code>, <strong></strong> 등) 제거.
 * 인라인 코드/볼드 끝에서 Enter 시 새 블록에 빈 <code>가 남아 다음 입력이 코드로 이어지는
 * 문제(사용자 체감: "하단에 코드블럭 자동 생성")를 막는다.
 */
function removeEmptyInlineWrappers(el) {
  if (!el) return;
  el.querySelectorAll("code, strong, b, em, i, u, s, del, span, a").forEach((n) => {
    if ((n.textContent || "") === "" && !n.querySelector("img, br")) n.remove();
  });
}

function convertToParagraph(block) {
  const c = directContent(block);
  const html = c ? c.innerHTML : "";
  const para = makeBlockElement("paragraph", { innerHTML: html, props: defaultProps("paragraph") });
  block.replaceWith(para);
  focusStart(directContent(para));
  return para;
}

export function handleEnter(e, onChange) {
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount) return;
  const content = e.target.closest(".content");
  if (!content) return;
  const block = content.closest(".block");
  if (!block) return;
  const type = block.dataset.type;
  e.preventDefault();

  if (type === "codeBlock") {
    const r = sel.getRangeAt(0);
    // 코드블록 탈출: 마지막 빈 줄에서 Enter(캐럿이 내용 끝 + 직전 문자 "\n") → 코드블록 밖 하단에 새 문단.
    // (Enter 두 번: 1회차 "\n" 삽입 → 빈 마지막 줄, 2회차 이 조건 성립 → 탈출)
    if (r.collapsed) {
      const beforeR = r.cloneRange();
      beforeR.selectNodeContents(content);
      beforeR.setEnd(r.endContainer, r.endOffset);
      const before = beforeR.toString();
      const afterR = r.cloneRange();
      afterR.selectNodeContents(content);
      afterR.setStart(r.endContainer, r.endOffset);
      const after = afterR.toString();
      if (after === "" && before.endsWith("\n")) {
        deleteTrailingNewline(content); // 탈출 트리거였던 마지막 "\n" 제거(코드 본문 보존)
        normalizeCodeTrailingNewline(content); // 남은 filler <br> 정리
        const next = makeBlockElement("paragraph", { innerHTML: "", props: defaultProps("paragraph") });
        block.after(next);
        focusStart(directContent(next));
        if (onChange) onChange();
        return;
      }
    }
    r.deleteContents();
    const t = document.createTextNode("\n");
    r.insertNode(t);
    r.setStartAfter(t);
    r.collapse(true);
    sel.removeAllRanges();
    sel.addRange(r);
    normalizeCodeTrailingNewline(content); // 후행 개행이면 filler <br>로 빈 줄 렌더 보정
    if (onChange) onChange();
    return;
  }

  // 토글(접을 수 있는) 헤딩/목록: Enter → 내용(children) 첫 블록으로 이동(제목 입력 후 본문 작성).
  const isToggle = block.dataset.toggle === "true" || type === "toggleListItem";
  if (isToggle) {
    block.classList.remove("collapsed"); // 접혀 있으면 펼침
    let kids = block.querySelector(":scope > .lumir-children");
    if (!kids) { kids = document.createElement("div"); kids.className = "lumir-children"; block.appendChild(kids); }
    // caret 뒤 텍스트(제목 분할분)를 새 첫 자식 단락으로 옮김
    const r0 = sel.getRangeAt(0);
    const tail0 = r0.cloneRange();
    tail0.selectNodeContents(content);
    tail0.setStart(r0.endContainer, r0.endOffset);
    const frag0 = tail0.extractContents();
    const child = makeBlockElement("paragraph", { innerHTML: "", props: defaultProps("paragraph") });
    directContent(child).appendChild(frag0);
    kids.insertBefore(child, kids.firstChild);
    focusStart(directContent(child));
    if (onChange) onChange();
    return;
  }

  // 토글 본문(children)에서 빈 마지막 줄에 Enter → 토글 밖으로 탈출: 빈 자식을 제거하고
  // 토글 블록의 다음 형제로 새 문단을 만든다("제목→본문→빈 줄에서 토글 밖 하단 블록" 흐름 완결).
  const kidsHost = block.parentElement && block.parentElement.classList && block.parentElement.classList.contains("lumir-children") ? block.parentElement : null;
  if (kidsHost && type === "paragraph" && !block.nextElementSibling && content.textContent.trim() === "" && !content.querySelector("img")) {
    const toggleBlock = kidsHost.closest(".block");
    if (toggleBlock) {
      block.remove();
      if (!kidsHost.children.length) kidsHost.remove();
      const next = makeBlockElement("paragraph", { innerHTML: "", props: defaultProps("paragraph") });
      toggleBlock.after(next);
      focusStart(directContent(next));
      if (onChange) onChange();
      return;
    }
  }

  const isList = LIST_TYPES.has(type);
  if (isList && content.textContent.trim() === "") {
    convertToParagraph(block); // 빈 리스트 항목에서 Enter → 문단
    if (onChange) onChange();
    return;
  }

  // caret에서 분할
  const range = sel.getRangeAt(0);
  const tail = range.cloneRange();
  tail.selectNodeContents(content);
  tail.setStart(range.endContainer, range.endOffset);
  const frag = tail.extractContents();

  const carryType = isList ? type : "paragraph";
  const next = makeBlockElement(carryType, { innerHTML: "", props: defaultProps(carryType) });
  const nc = directContent(next);
  nc.appendChild(frag);
  removeEmptyInlineWrappers(nc); // 새 블록의 빈 <code> 등 제거(코드 이어짐 방지)
  removeEmptyInlineWrappers(content); // 원본 블록의 빈 서식 래퍼도 정리
  block.after(next);
  focusStart(nc);
  if (onChange) onChange();
}

/**
 * Shift+Enter: 블록 분할 없이 한 줄바꿈(HardBreak)을 삽입한다.
 * 모델 표현은 인라인 텍스트 내 "\n"(직렬화 시 walkInline에서 <br>↔"\n" 라운드트립).
 * codeBlock은 Enter와 동일하게 "\n"(이미 줄바꿈 동작)을 넣고, 그 외 블록은 <br>를 caret에 삽입.
 */
function handleHardBreak(e, onChange) {
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount) return;
  const content = e.target.closest(".content");
  if (!content) return;
  const block = content.closest(".block");
  if (!block) return;
  e.preventDefault();
  const r = sel.getRangeAt(0);
  r.deleteContents();
  if (block.dataset.type === "codeBlock") {
    const t = document.createTextNode("\n");
    r.insertNode(t);
    r.setStartAfter(t);
    r.collapse(true);
    sel.removeAllRanges();
    sel.addRange(r);
    normalizeCodeTrailingNewline(content); // 후행 개행 렌더 보정(pre-wrap)
    if (onChange) onChange();
    return;
  } else {
    const br = document.createElement("br");
    r.insertNode(br);
    // insertNode가 끝 오프셋에서 빈 텍스트 노드를 br 뒤에 남길 수 있음 → 제거(끝 판정 정확화)
    while (br.nextSibling && br.nextSibling.nodeType === 3 && br.nextSibling.nodeValue === "") br.nextSibling.remove();
    // 줄 끝(다음 형제 없음)이면 브라우저가 후행 <br> 뒤 caret을 새 줄에 두지 못해 입력이 br 앞으로
    // 들어가는 문제가 있다. 마킹된 보조 <br>(filler)를 덧대 caret을 실제 br과 filler 사이에 둔다.
    // filler는 직렬화에서 무시(walkInline)되어 라운드트립을 오염시키지 않는다.
    if (!br.nextSibling) {
      const filler = document.createElement("br");
      filler.setAttribute("data-lumir-br-filler", "");
      br.after(filler);
    }
    r.setStartAfter(br);
  }
  r.collapse(true);
  sel.removeAllRanges();
  sel.addRange(r);
  if (onChange) onChange();
}

function handleBackspace(e, onChange) {
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount || !sel.isCollapsed) return;
  const content = e.target.closest(".content");
  if (!content) return;
  const block = content.closest(".block");
  if (!block) return;
  if (!caretAtStart(content)) return;
  e.preventDefault();

  const type = block.dataset.type;
  if (type !== "paragraph") {
    convertToParagraph(block); // 비문단 → 문단(타입 해제)
    if (onChange) onChange();
    return;
  }

  const prev = block.previousElementSibling;
  if (!prev) {
    // 토글 본문 첫 자식에서 Backspace(갇힘 해소): 빈 줄이면 제거하고 캐럿을 토글 제목 끝으로.
    const kidsHost = block.parentElement;
    if (kidsHost && kidsHost.classList && kidsHost.classList.contains("lumir-children")
        && content.textContent.trim() === "" && !content.querySelector("img")) {
      const toggleBlock = kidsHost.closest(".block");
      const title = toggleBlock && directContent(toggleBlock);
      block.remove();
      if (!kidsHost.children.length) kidsHost.remove();
      if (title) focusEnd(title);
      if (onChange) onChange();
    }
    return;
  }
  if (prev.dataset.type === "table") {
    const cells = prev.querySelectorAll("td.lumir-cell");
    if (cells.length) focusEnd(cells[cells.length - 1]);
    return; // 표와는 병합하지 않음
  }
  if (prev.dataset.type === "columnList") {
    // 컬럼 컨테이너(.content.lumir-columns)에는 병합하지 않음 — 마지막 편집영역으로 캐럿만 이동(M3).
    const conts = prev.querySelectorAll(".content[contenteditable='true']");
    if (conts.length) focusEnd(conts[conts.length - 1]);
    return;
  }
  let prevContent = directContent(prev);
  // prev가 중첩 children(.lumir-children)을 가지면 시각적 말단(마지막 자손 블록)의 content로 병합(M3).
  const prevKids = prev.querySelector(":scope > .lumir-children");
  if (prevKids) {
    const nested = prevKids.querySelectorAll(".block");
    const lastNested = nested[nested.length - 1];
    const lc = lastNested && directContent(lastNested);
    if (lc) prevContent = lc;
  }
  if (!prevContent) return;

  focusEnd(prevContent);
  const j = window.getSelection().getRangeAt(0);
  const moving = document.createRange();
  moving.selectNodeContents(content);
  const frag = moving.extractContents();
  j.insertNode(frag);
  try {
    const r = document.createRange();
    r.setStart(j.startContainer, j.startOffset);
    r.collapse(true);
    const s = window.getSelection();
    s.removeAllRanges();
    s.addRange(r);
  } catch (_) {
    /* caret 복원 best-effort */
  }
  prevContent.normalize();
  block.remove();
  if (onChange) onChange();
}

/**
 * 에디터 요소에 편집 키 핸들러 부착. opts: { onChange?, isComposing? }
 * 반환: 해제 함수.
 */
/** 블록 텍스트 정렬 설정(left/center/right/justify). __props·data-text-alignment 반영. */
export function setBlockAlignment(block, align) {
  if (!block) return false;
  if (!block.__props) block.__props = { textColor: "default", backgroundColor: "default", textAlignment: "left" };
  block.__props.textAlignment = align;
  if (align && align !== "left") block.dataset.textAlignment = align;
  else block.removeAttribute("data-text-alignment");
  return true;
}

/** 현재 블록을 직전 형제 블록의 .lumir-children 으로 들여쓰기(중첩) */
export function indentBlock(block) {
  const prev = block.previousElementSibling;
  if (!prev || !prev.classList || !prev.classList.contains("block")) return false;
  let kids = prev.querySelector(":scope > .lumir-children");
  if (!kids) { kids = document.createElement("div"); kids.className = "lumir-children"; prev.appendChild(kids); }
  kids.appendChild(block);
  focusEnd(directContent(block));
  return true;
}
/** 중첩 블록을 부모 블록 다음으로 내어쓰기 */
export function outdentBlock(block) {
  const kids = block.parentElement;
  if (!kids || !kids.classList || !kids.classList.contains("lumir-children")) return false;
  const parentBlock = kids.parentElement;
  if (!parentBlock) return false;
  parentBlock.after(block);
  if (!kids.querySelector(":scope > .block")) kids.remove();
  focusEnd(directContent(block));
  return true;
}

export function attachEditing(editorEl, opts) {
  opts = opts || {};
  const onChange = typeof opts.onChange === "function" ? opts.onChange : null;
  const isComposing = typeof opts.isComposing === "function" ? opts.isComposing : () => false;
  const tabBehavior = opts.tabBehavior === "prefer-indent" ? "prefer-indent" : "prefer-navigate-ui";
  const cross = opts.cross || null; // 크로스블록 선택 서식 파사드

  function onKeydown(e) {
    if (e.isComposing || isComposing()) return; // IME 가드

    // 인라인 서식 단축(Ctrl/Cmd+B/I/U) — 블록·표 셀 공통
    const mod = e.metaKey || e.ctrlKey;
    if (mod && !e.altKey) {
      const mk = { b: "bold", i: "italic", u: "underline" }[e.key.toLowerCase()];
      if (mk) {
        // 크로스블록(여러 줄) 선택이면 일괄 적용 경로로 라우팅.
        if (cross && cross.isActive()) {
          e.preventDefault();
          cross.applyMark(mk);
          return;
        }
        const editable = e.target.closest && e.target.closest(".content, td.lumir-cell");
        if (editable && editorEl.contains(editable)) {
          e.preventDefault();
          if (applyInlineMark(editable, mk) && onChange) onChange();
          return;
        }
      }
    }

    // 블록 타입 단축 (blocknote 정합) — Ctrl/Cmd+Alt+0~3=문단/제목, Ctrl/Cmd+Shift+6~9=토글목록/번호/글머리/할일.
    // Shift+숫자는 '&' 등으로 바뀌므로 e.code(물리 키)로 판정. 표 셀·표 내부는 제외.
    if (mod) {
      let conv = null;
      if (e.altKey && !e.shiftKey) {
        conv = { Digit0: { type: "paragraph" }, Digit1: { type: "heading", level: 1 }, Digit2: { type: "heading", level: 2 }, Digit3: { type: "heading", level: 3 } }[e.code] || null;
      } else if (e.shiftKey && !e.altKey) {
        conv = { Digit6: { type: "toggleListItem" }, Digit7: { type: "numberedListItem" }, Digit8: { type: "bulletListItem" }, Digit9: { type: "checkListItem" } }[e.code] || null;
      }
      if (conv) {
        const content = e.target.closest && e.target.closest(".content");
        // 표 셀에서는 블록 타입 변환 금지(가드는 셀=e.target 기준; content는 표 블록 래퍼라 .lumir-tbl을 위로 못 찾음).
        if (content && editorEl.contains(content) && !(e.target.closest && e.target.closest(".lumir-tbl"))) {
          const block = content.closest(".block");
          if (block) {
            e.preventDefault();
            changeBlockType(block, conv.type, propsForType(conv.type, conv.level));
            if (onChange) onChange();
            return;
          }
        }
      }
    }

    // 블록 편집(Enter/Backspace) — 표 셀은 P3에서 처리
    if (e.target.closest && e.target.closest(".lumir-tbl")) return;
    const content = e.target.closest && e.target.closest(".content");
    if (!content || !editorEl.contains(content)) return;
    if (e.key === "Tab" && tabBehavior === "prefer-indent" && !e.defaultPrevented) {
      const block = content.closest(".block");
      if (block) {
        e.preventDefault();
        const ok = e.shiftKey ? outdentBlock(block) : indentBlock(block);
        if (ok && onChange) onChange();
        return;
      }
    }
    if (e.key === "Enter" && !e.shiftKey) handleEnter(e, onChange);
    else if (e.key === "Enter" && e.shiftKey) handleHardBreak(e, onChange);
    else if (e.key === "Backspace") handleBackspace(e, onChange);
  }

  editorEl.addEventListener("keydown", onKeydown);
  return () => editorEl.removeEventListener("keydown", onKeydown);
}

// --- 마크다운 단축 (P1-③) ---
const MD_RULES = [
  { re: /^#\s$/, type: "heading", level: 1 },
  { re: /^##\s$/, type: "heading", level: 2 },
  { re: /^###\s$/, type: "heading", level: 3 },
  { re: /^####\s$/, type: "heading", level: 4 },
  { re: /^#####\s$/, type: "heading", level: 5 },
  { re: /^######\s$/, type: "heading", level: 6 },
  { re: /^[-*]\s$/, type: "bulletListItem" },
  { re: /^1\.\s$/, type: "numberedListItem" },
  { re: /^\[\s?\]\s$/, type: "checkListItem" },
  { re: /^>\s$/, type: "quote" },
  { re: /^```$/, type: "codeBlock" },
];

export function propsForType(type, level, isToggle) {
  if (type === "heading") {
    const p = { ...BASE_PROPS(), level: level || 1 };
    if (isToggle) p.isToggleable = true; // blocknote 토글 헤딩 prop명과 일치
    return p;
  }
  return defaultProps(type);
}

export function changeBlockType(block, type, props) {
  const el = makeBlockElement(type, { innerHTML: "", props });
  block.replaceWith(el);
  focusStart(directContent(el));
  return el;
}

/** 블록 타입 변경 — 기존 인라인 내용 보존(툴바/FloatingMenu용). */
export function setBlockType(block, type, level, isToggle) {
  const content = block.querySelector(":scope > .content");
  const html = content ? content.innerHTML : "";
  const el = makeBlockElement(type, { innerHTML: html, props: propsForType(type, level, isToggle) });
  block.replaceWith(el);
  focusStart(directContent(el));
  return el;
}

/**
 * 문단 시작의 마크다운 트리거(`# `,`## `,`- `/`* `,`1. `,`[] `,`> `,``` ``` ```) 감지 시
 * 블록 타입 변환(트리거 텍스트 소거). 변환되면 true. (문단에서만 동작)
 */
export function tryMarkdown(content, headingLevels) {
  if (!content) return false;
  const block = content.closest && content.closest(".block");
  if (!block || block.dataset.type !== "paragraph") return false;
  const t = content.textContent;
  for (const r of MD_RULES) {
    if (r.type === "heading" && Array.isArray(headingLevels) && !headingLevels.includes(r.level)) continue; // 허용 레벨만
    if (r.re.test(t)) {
      changeBlockType(block, r.type, propsForType(r.type, r.level));
      return true;
    }
  }
  return false;
}

// --- 인라인 마크다운 자동변환 (A4): `**굵게**` `*기울임*` `__굵게__` `_기울임_` `` `코드` `` `~~취소~~` ---
// 닫는 구분자를 막 입력한 시점, caret 바로 앞의 "한 텍스트 노드 내 평문" 패턴만 변환.
// (혼합 인라인/경계 교차는 의도적으로 건드리지 않음 — BlockNote inputrule과 동일 범위)
// lookbehind로 `**x*`(굵게 입력 중 첫 닫는 별표)에서 기울임이 조기 변환되는 것을 방지.
const INLINE_MD_RULES = [
  { tag: "strong", re: /\*\*([^\s*](?:[^*]*[^\s*])?)\*\*$/ },
  { tag: "strong", re: /(?<!_)__([^\s_](?:[^_]*[^\s_])?)__$/ },
  { tag: "s", re: /~~([^\s~](?:[^~]*[^\s~])?)~~$/ },
  { tag: "code", re: /`([^\s`](?:[^`]*[^\s`])?)`$/ },
  { tag: "em", re: /(?<!\*)\*([^\s*](?:[^*]*[^\s*])?)\*$/ },
  { tag: "em", re: /(?<!_)_([^\s_](?:[^_]*[^\s_])?)_$/ },
];

/**
 * caret 앞 텍스트의 완성된 인라인 마크다운 패턴을 마크 요소로 치환한다. 변환되면 true.
 * 코드블록에서는 동작하지 않는다(코드 내용 보존).
 */
export function tryInlineMarkdown(content) {
  if (!content) return false;
  const block = content.closest && content.closest(".block");
  if (!block || block.dataset.type === "codeBlock") return false;
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount) return false;
  const range = sel.getRangeAt(0);
  if (!range.collapsed) return false;
  const node = range.endContainer;
  if (!node || node.nodeType !== 3 || !content.contains(node)) return false; // 텍스트 노드 내에서만
  const offset = range.endOffset;
  const before = node.nodeValue.slice(0, offset);
  for (const rule of INLINE_MD_RULES) {
    const m = rule.re.exec(before);
    if (!m) continue;
    const start = offset - m[0].length;
    const mark = document.createElement(rule.tag);
    mark.textContent = m[1];
    const r = document.createRange();
    r.setStart(node, start);
    r.setEnd(node, offset);
    r.deleteContents();
    r.insertNode(mark);
    const after = document.createRange();
    after.setStartAfter(mark);
    after.collapse(true);
    sel.removeAllRanges();
    sel.addRange(after);
    return true;
  }
  return false;
}
